/**
 * Chart theme: the colour slots, the sequential ramp, mark sizes and per-kind heights (docs/reports/design.md §2).
 * Owner: slice web-charts (#1115). The values live in charts.css as CSS variables so dark mode swaps them.
 */

/** The categorical slots, in fixed assignment order. */
export const CHART_SLOTS: string[] = [
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
  '--chart-6',
  '--chart-7',
  '--chart-8',
]

/** The sequential ramp steps, light (less) to dark (more) in light mode; charts.css reverses the feel in dark mode. */
export const SEQ_SLOTS: string[] = ['--chart-seq-1', '--chart-seq-2', '--chart-seq-3', '--chart-seq-4', '--chart-seq-5']

/**
 * Colour of the series at `index` in the chart's own series list (its entity order, before any role filtering), so a
 * filter that hides one series never repaints the survivors. A ninth series is never a generated hue: it is neutral.
 */
export function seriesColor(index: number): string {
  const slot = CHART_SLOTS[index]
  return slot ? `var(${slot})` : 'var(--chart-other)'
}

/** Sequential step for a magnitude in [0, 1]; `floor` skips the faintest steps (bars need contrast on the surface). */
export function seqColor(t: number, floor = 0): string {
  const steps = SEQ_SLOTS.length
  const clamped = Math.min(1, Math.max(0, t))
  const i = Math.min(steps - 1, floor + Math.round(clamped * (steps - 1 - floor)))
  return `var(${SEQ_SLOTS[i]})`
}

export const MARK = {
  /** Max bar thickness. */
  barMax: 24,
  /** Rounded data end. */
  radius: 4,
  /** Surface gap between touching bars and stacked segments. */
  gap: 2,
  line: 2,
  marker: 8,
  ring: 2,
  areaOpacity: 0.1,
} as const

export const HEIGHT = {
  line: 220,
  area: 220,
  bar: 220,
  stacked_bar: 220,
  histogram: 220,
  hbarRow: 28,
  heatmapRow: 24,
} as const

export const MARGIN = { top: 10, right: 12, bottom: 26, left: 48 } as const

/** Text tokens: values, labels and legends wear these, never the series colour. */
export const INK = {
  primary: 'var(--color-foreground)',
  secondary: 'var(--color-muted-foreground)',
  grid: 'var(--color-border)',
  surface: 'var(--color-card)',
} as const
